import type { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';

import { COLORS } from '../../IndividualPlan/tokens';
import type { PeDomainOverlayYear, PeTrajectoryPoint, ScoreUnit } from '../types';
import { FlashlightTrajectoryChart } from './charts/FlashlightTrajectoryChart';
import { WidaTrajectoryChart } from './charts/WidaTrajectoryChart';
import { formatSchoolYearShort } from './charts/YearAxisTick';

export const PER_DOMAIN_CHART_FONT_SIZE = 20;

type PerDomainCardBaseProps = {
  widaTrajectory: PeTrajectoryPoint[];
  unit: ScoreUnit;
  threshold?: number | null;
};

type PerDomainCardProps = PerDomainCardBaseProps &
  (
    | {
        variant?: 'paired';
        domainLabel: 'Speaking' | 'Writing';
        flashlightOverlay?: PeDomainOverlayYear;
      }
    | {
        variant: 'wida-only';
        domainLabel: 'Listening' | 'Reading';
        flashlightOverlay?: never;
      }
  );

const FLASHLIGHT_UNAVAILABLE = 'Flashlight data not yet available for this district';

function NoteBox({ testId, title, body }: { testId: string; title?: string; body: string }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        backgroundColor: '#FFFFFF',
        border: `1px solid ${COLORS.hairline}`,
        borderRadius: 2,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      {title && (
        <Typography component="p" sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
          {title}
        </Typography>
      )}
      <Typography component="p" sx={{ fontSize: 16, color: COLORS.muted }}>
        {body}
      </Typography>
    </Box>
  );
}

type DomainSummary = { currentText: string; deltaText: string | null };

function summarize(trajectory: PeTrajectoryPoint[], unit: ScoreUnit): DomainSummary | null {
  if (trajectory.length === 0) return null;
  const value = (p: PeTrajectoryPoint) =>
    unit === 'scale_score' ? p.average_scale_score : p.average_proficiency_level;
  const format = (n: number) => (unit === 'scale_score' ? String(Math.round(n)) : n.toFixed(1));

  const last = trajectory[trajectory.length - 1]!;
  const prev = trajectory[trajectory.length - 2];
  const current = value(last);

  // A delta across the reset compares two different rulers.
  let deltaText: string | null = null;
  if (prev && prev.reset_era === last.reset_era) {
    const delta = current - value(prev);
    const magnitude = unit === 'scale_score' ? Math.round(Math.abs(delta)) : Math.abs(delta).toFixed(1);
    deltaText = `${delta >= 0 ? '+' : '−'}${magnitude} vs ${formatSchoolYearShort(prev.school_year)}`;
  }

  return { currentText: format(current), deltaText };
}

function Divider() {
  return <Box aria-hidden sx={{ height: '1px', width: '100%', backgroundColor: COLORS.hairline }} />;
}

function Legend({ color, children }: { color: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      <Typography component="span" sx={{ fontSize: 16, color: COLORS.muted }}>
        {children}
      </Typography>
    </Box>
  );
}

export function PerDomainCard({
  domainLabel,
  widaTrajectory,
  unit,
  threshold,
  variant = 'paired',
  flashlightOverlay,
}: PerDomainCardProps) {
  const isWidaOnly = variant === 'wida-only';
  const summary = summarize(widaTrajectory, unit);
  const accessLabel =
    unit === 'scale_score' ? 'Avg. ACCESS scale score' : 'Avg. ACCESS Prof. Level score';

  return (
    <Box
      data-testid="per-domain-card"
      sx={{
        backgroundColor: COLORS.bg,
        borderRadius: 2,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Typography
        variant="h6"
        component="h3"
        data-testid="per-domain-header"
        sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}
      >
        {domainLabel}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {summary && (
          <Box data-testid="per-domain-summary" sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography
              component="span"
              sx={{ fontSize: 32, fontWeight: 800, color: COLORS.ink, letterSpacing: '-0.5px', lineHeight: 1 }}
            >
              {summary.currentText}
            </Typography>
            {summary.deltaText && (
              <Typography component="span" sx={{ fontSize: 12, color: COLORS.muted }}>
                {summary.deltaText}
              </Typography>
            )}
          </Box>
        )}

        <Divider />

        <Box data-testid="wida-chart" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Legend color={COLORS.blue}>{accessLabel}</Legend>
          <WidaTrajectoryChart
            trajectory={widaTrajectory}
            unit={unit}
            threshold={threshold}
            fontSize={PER_DOMAIN_CHART_FONT_SIZE}
            variant="perDomain"
          />
        </Box>

        {!isWidaOnly && (
          <>
            <Divider />
            {flashlightOverlay ? (
              <Box data-testid="flashlight-chart" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Legend color={COLORS.navy}>Avg. Flashlight Score</Legend>
                <FlashlightTrajectoryChart
                  overlay={flashlightOverlay}
                  domainLabel={domainLabel}
                  fontSize={PER_DOMAIN_CHART_FONT_SIZE}
                />
              </Box>
            ) : (
              <NoteBox testId="flashlight-unavailable-note" body={FLASHLIGHT_UNAVAILABLE} />
            )}
          </>
        )}

        {isWidaOnly && (
          <NoteBox
            testId="wida-only-pilot-note"
            title={`${domainLabel} Benchmark Data Is Not Yet Available`}
            body={`${domainLabel} benchmarks are being piloted during the ’26-27 school year.`}
          />
        )}
      </Box>
    </Box>
  );
}
